import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Divider,
  Chip,
  Skeleton,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import LaptopIcon from '@mui/icons-material/Laptop';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useActiveSessions,
  useRevokeSession,
  useRevokeOtherSessions,
} from '../hooks/useSessions';
import type { Session } from '../hooks/useSessions';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { authClient } from '../lib/auth';
import { useFlag } from '../lib/flags';
import QRCode from 'qrcode';

const emailPasswordEnabled = import.meta.env.VITE_EMAIL_PASSWORD_ENABLED === 'true';

dayjs.extend(relativeTime);

function parseDevice(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  let browser = 'Browser';
  let os = 'Unknown OS';

  if (ua.includes('chrome') && !ua.includes('edg')) browser = 'Chrome';
  else if (ua.includes('firefox')) browser = 'Firefox';
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
  else if (ua.includes('edg')) browser = 'Edge';

  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os')) os = 'macOS';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('linux')) os = 'Linux';

  return `${os} · ${browser}`;
}

interface SessionItemProps {
  session: Session;
  isCurrentSession: boolean;
}

function SessionItem({ session, isCurrentSession }: SessionItemProps) {
  const { mutate: revokeSession, isPending } = useRevokeSession();

  return (
    <ListItem alignItems="flex-start" sx={{ py: 2 }}>
      <LaptopIcon sx={{ mr: 2, mt: 0.5, color: 'text.secondary' }} />
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1">
              {parseDevice(session.userAgent)}
            </Typography>
            {isCurrentSession && (
              <Chip label="This device" size="small" color="primary" />
            )}
          </Box>
        }
        secondary={
          <>
            <Typography component="span" display="block" variant="body2" color="text.secondary">
              IP: {session.ipAddress}
            </Typography>
            <Typography component="span" display="block" variant="body2" color="text.secondary">
              Last active {dayjs(session.lastActiveAt).fromNow()}
            </Typography>
          </>
        }
      />
      <ListItemSecondaryAction>
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={() => revokeSession(session.id)}
          disabled={isCurrentSession || isPending}
        >
          {isCurrentSession ? 'Current' : 'Revoke'}
        </Button>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

type TwoFAStep = 'idle' | 'enabling-password' | 'enabling-qr' | 'enabling-codes' | 'disabling-password';

interface TwoFactorSectionProps {
  isEnabled: boolean;
  onStatusChange: () => void;
}

function TwoFactorSection({ isEnabled, onStatusChange }: TwoFactorSectionProps) {
  const [step, setStep] = useState<TwoFAStep>('idle');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  // Reset state when section is closed
  const reset = () => {
    setStep('idle');
    setPassword('');
    setTotpCode('');
    setError(null);
    setQrDataUrl(null);
    setBackupCodes([]);
  };

  const handleEnable = async () => {
    setError(null);
    if (!password) return;
    setLoading(true);
    try {
      const result = await (authClient as any).twoFactor.enable({ password });
      if (result?.error) {
        setError(result.error.message ?? 'Failed to enable 2FA.');
        return;
      }
      const totpUri: string = result?.data?.totpURI ?? result?.data?.totpUri ?? '';
      const codes: string[] = result?.data?.backupCodes ?? [];
      if (totpUri) {
        const dataUrl = await QRCode.toDataURL(totpUri, { width: 200, margin: 2 });
        setQrDataUrl(dataUrl);
      }
      setBackupCodes(codes);
      setPassword('');
      setStep('enabling-qr');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable 2FA.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEnable = async () => {
    setError(null);
    if (!totpCode.trim()) return;
    setLoading(true);
    try {
      const result = await (authClient as any).twoFactor.verifyTotp({ code: totpCode.trim() });
      if (result?.error) {
        setError(result.error.message ?? 'Invalid code. Please try again.');
        return;
      }
      setTotpCode('');
      setStep('enabling-codes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setError(null);
    if (!password) return;
    setLoading(true);
    try {
      const result = await (authClient as any).twoFactor.disable({ password });
      if (result?.error) {
        setError(result.error.message ?? 'Failed to disable 2FA.');
        return;
      }
      onStatusChange();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onStatusChange();
    reset();
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        {step === 'idle' && (
          <>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h6">Two-factor authentication</Typography>
              {isEnabled && <Chip label="Active" size="small" color="success" />}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {isEnabled
                ? 'Your account is protected with an authenticator app.'
                : 'Add an extra layer of security using an authenticator app (Google Authenticator, Authy, etc.).'}
            </Typography>
            {isEnabled ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => { setStep('disabling-password'); setError(null); }}
              >
                Disable 2FA
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => { setStep('enabling-password'); setError(null); }}
              >
                Enable two-factor authentication
              </Button>
            )}
          </>
        )}

        {step === 'enabling-password' && (
          <>
            <Typography variant="h6" mb={1}>Enable two-factor authentication</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your account password to continue.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Current password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { void handleEnable(); } }}
                fullWidth
                autoFocus
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={() => { void handleEnable(); }}
                  disabled={loading || !password}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                  Continue
                </Button>
                <Button variant="text" onClick={reset} disabled={loading}>Cancel</Button>
              </Box>
            </Box>
          </>
        )}

        {step === 'enabling-qr' && (
          <>
            <Typography variant="h6" mb={1}>Scan the QR code</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Open your authenticator app, scan this QR code, then enter the 6-digit code below to activate 2FA.
            </Typography>
            {qrDataUrl && (
              <Box mb={3} display="flex" justifyContent="flex-start">
                <img src={qrDataUrl} alt="Scan with your authenticator app" width={200} height={200} />
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="6-digit code from your app"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { void handleVerifyEnable(); } }}
                slotProps={{ htmlInput: { maxLength: 6 } }}
                autoComplete="one-time-code"
                fullWidth
                autoFocus
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={() => { void handleVerifyEnable(); }}
                  disabled={loading || totpCode.trim().length !== 6}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                  Activate 2FA
                </Button>
                <Button variant="text" onClick={reset} disabled={loading}>Cancel</Button>
              </Box>
            </Box>
          </>
        )}

        {step === 'enabling-codes' && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>Two-factor authentication is now active.</Alert>
            <Typography variant="subtitle2" mb={1}>Save your backup codes</Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Store these codes somewhere safe. Each code can only be used once if you lose access to your authenticator app.
            </Alert>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.5,
                mb: 3,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {backupCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </Box>
            <Button variant="contained" onClick={handleDone}>
              Done, I&apos;ve saved my codes
            </Button>
          </>
        )}

        {step === 'disabling-password' && (
          <>
            <Typography variant="h6" mb={1}>Disable two-factor authentication</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your account password to confirm.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Current password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { void handleDisable(); } }}
                fullWidth
                autoFocus
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => { void handleDisable(); }}
                  disabled={loading || !password}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                  Confirm disable
                </Button>
                <Button variant="text" onClick={reset} disabled={loading}>Cancel</Button>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function SecuritySettingsPage() {
  const { data: sessions, isLoading, error } = useActiveSessions();
  const { mutate: revokeOthers, isPending: isRevokingAll } =
    useRevokeOtherSessions();
  const user = useAuthStore((s) => s.user);
  const twoFactorEnabled = useFlag('two-factor');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const { data: passwordStatus, isLoading: pwStatusLoading, refetch: refetchPasswordStatus } = useQuery({
    queryKey: ['auth', 'has-password'],
    queryFn: () => api.get<{ hasPassword: boolean }>('/api/v1/auth/has-password').then(r => r.data),
    enabled: emailPasswordEnabled,
  });
  const hasPassword = passwordStatus?.hasPassword ?? false;

  // Track 2FA enabled state locally so the section re-renders without a full page reload.
  const [twoFAEnabled, setTwoFAEnabled] = useState<boolean>(user?.twoFactorEnabled ?? false);
  useEffect(() => {
    setTwoFAEnabled(user?.twoFactorEnabled ?? false);
  }, [user?.twoFactorEnabled]);

  const handlePasswordSubmit = async () => {
    setPwError(null);
    if (newPassword.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      if (hasPassword) {
        await api.post('/api/v1/auth/change-password', { currentPassword, newPassword });
      } else {
        await api.post('/api/v1/auth/set-password', { newPassword });
      }
    } catch (err) {
      setPwLoading(false);
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setPwError(msg ?? 'Failed to update password');
      return;
    }
    setPwLoading(false);
    setPwSuccess(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // The current session is identified as the most recently active one.
  const sessionList = Array.isArray(sessions) ? sessions : [];
  const currentSessionId = sessionList.length > 0
    ? sessionList.reduce(
        (latest, s) =>
          new Date(s.lastActiveAt) > new Date(latest.lastActiveAt) ? s : latest,
        sessionList[0],
      )?.id
    : undefined;

  const handleTwoFAStatusChange = () => {
    // Refresh password status (also triggers re-read of user from session in parent if needed)
    void refetchPasswordStatus();
    // Toggle local 2FA state optimistically
    setTwoFAEnabled((prev) => !prev);
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={1}>
        Security Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Manage your active sessions. Revoke access from devices you don&apos;t
        recognize.
      </Typography>

      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Active Sessions</Typography>
            {sessionList.length > 1 && (
              <Button
                color="error"
                variant="outlined"
                size="small"
                onClick={() => revokeOthers()}
                disabled={isRevokingAll}
              >
                Log out all other devices
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load sessions.
            </Alert>
          )}

          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height={72} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
            <List disablePadding>
              {sessionList.map((session, i) => (
                <Box key={session.id}>
                  {i > 0 && <Divider />}
                  <SessionItem
                    session={session}
                    isCurrentSession={session.id === currentSessionId}
                  />
                </Box>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {emailPasswordEnabled && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            {pwStatusLoading ? (
              <Skeleton height={200} />
            ) : pwSuccess ? (
              <Alert severity="success">Password {hasPassword ? 'changed' : 'set'} successfully.</Alert>
            ) : (
              <>
                <Typography variant="h6" mb={1}>
                  {hasPassword ? 'Change Password' : 'Set Password'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {hasPassword
                    ? 'Update your password. You will need your current password.'
                    : 'Add a password to your account so you can sign in without a magic link.'}
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  {hasPassword && (
                    <TextField
                      label="Current password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      fullWidth
                    />
                  )}
                  <TextField
                    label="New password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Confirm password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                  />
                  {pwError && <Alert severity="error">{pwError}</Alert>}
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => { void handlePasswordSubmit(); }}
                      disabled={pwLoading}
                      startIcon={pwLoading ? <CircularProgress size={16} /> : undefined}
                    >
                      {hasPassword ? 'Change password' : 'Set password'}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {twoFactorEnabled && hasPassword && (
        <TwoFactorSection
          isEnabled={twoFAEnabled}
          onStatusChange={handleTwoFAStatusChange}
        />
      )}
    </Box>
  );
}
